import { useMyRequisitionReturns } from "../hooks/useRequisition";
import {
  RequisitionReturnApprovalStatus,
  type RequisitionItemReturn,
} from "../services/requisitionApi";
import { LoadingAnimation } from "@/shared/components/LoadingAnimation";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const statusVariant = (
  s: number
): "default" | "secondary" | "outline" | "destructive" => {
  switch (s) {
    case RequisitionReturnApprovalStatus.Approved:
      return "default";
    case RequisitionReturnApprovalStatus.Rejected:
      return "destructive";
    case RequisitionReturnApprovalStatus.PendingApproval:
      return "secondary";
    default:
      return "outline";
  }
};

const statusLabel = (s: number): string => {
  switch (s) {
    case RequisitionReturnApprovalStatus.Approved:
      return "Approved";
    case RequisitionReturnApprovalStatus.Rejected:
      return "Rejected";
    case RequisitionReturnApprovalStatus.PendingApproval:
      return "Pending approval";
    case RequisitionReturnApprovalStatus.Draft:
      return "Draft";
    default:
      return "—";
  }
};

const groupByStatus = (rows: RequisitionItemReturn[]) => {
  const out = {
    pending: [] as RequisitionItemReturn[],
    decided: [] as RequisitionItemReturn[],
  };
  for (const r of rows) {
    if (r.status === RequisitionReturnApprovalStatus.PendingApproval) out.pending.push(r);
    else out.decided.push(r);
  }
  return out;
};

export const MyReturnsPage = () => {
  const { data: returns = [], isLoading, error } = useMyRequisitionReturns();

  if (isLoading) return <LoadingAnimation fullScreen />;
  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4">
        <p className="text-red-600">Failed to load your returns</p>
      </div>
    );
  }

  const { pending, decided } = groupByStatus(returns);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">My Returns</h1>
        <p className="text-gray-500 mt-1">
          Returns you've submitted for unused items. Pending entries are with
          the store manager; approved entries have already credited stock or
          the customer's seed batch.
        </p>
      </div>

      <Section title={`Pending (${pending.length})`} rows={pending} />
      <Section title={`Approved & Rejected (${decided.length})`} rows={decided} />
    </div>
  );
};

const Section = ({
  title,
  rows,
}: {
  title: string;
  rows: RequisitionItemReturn[];
}) => (
  <div className="space-y-2">
    <h2 className="text-sm font-semibold text-muted-foreground">{title}</h2>
    <div className="rounded-lg border overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Item</TableHead>
            <TableHead>Source</TableHead>
            <TableHead>Sowing</TableHead>
            <TableHead className="text-right">Qty</TableHead>
            <TableHead>Submitted</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Notes / Reason</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={7} className="text-center text-muted-foreground py-6">
                Nothing here.
              </TableCell>
            </TableRow>
          )}
          {rows.map((r) => (
            <TableRow key={r.id}>
              <TableCell>
                <div className="font-medium">{r.inputName}</div>
                <div className="text-xs text-muted-foreground">
                  Requisition {r.requisitionNumber}
                </div>
              </TableCell>
              <TableCell>
                {r.isCustomerSeedReturn ? (
                  <Badge variant="outline">🌱 {r.seedBatchNumber}</Badge>
                ) : (
                  <Badge variant="secondary">Company stock</Badge>
                )}
              </TableCell>
              <TableCell className="text-sm">
                {r.sowingInstructionNumber ?? "—"}
              </TableCell>
              <TableCell className="text-right text-sm font-semibold">
                {r.quantity} {r.unitOfMeasure}
              </TableCell>
              <TableCell className="text-xs text-muted-foreground">
                <div>{new Date(r.submittedAt).toLocaleString()}</div>
                {r.returnedDate && (
                  <div className="text-[10px]">
                    Returned {new Date(r.returnedDate).toLocaleDateString()}
                  </div>
                )}
              </TableCell>
              <TableCell>
                <Badge variant={statusVariant(r.status)}>{statusLabel(r.status)}</Badge>
                {r.approvedAt && (
                  <div className="text-[10px] text-muted-foreground mt-0.5">
                    {new Date(r.approvedAt).toLocaleDateString()} ·{" "}
                    {r.approvedByUserName ?? ""}
                  </div>
                )}
              </TableCell>
              <TableCell className="text-xs text-muted-foreground max-w-xs">
                {r.status === RequisitionReturnApprovalStatus.Rejected
                  ? r.rejectionReason
                  : r.approvalNotes || r.submissionNotes || "—"}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  </div>
);
